'use client';

import cx from 'classnames';
import {useEffect, useMemo, useRef, useState} from 'react';
import {Button} from '../atoms/Button';
import {InputText} from '../atoms/InputText';
import {Link} from '../atoms/Link';
import {Typography} from '../atoms/Typography';
import {useGiphyData} from '../hooks/useGiphyData';
import {cloud} from '../sass/base/_exports.sass';
import {CloseCircleIcon} from '../svg/CloseCircleIcon';

export const Giphy = ({onHandleGifSelect}) => {
  const [search, setSearch] = useState('');
  const {giphyResultsRef, results, searchLinks, loading, error} = useGiphyData(search);
  const linksWrapperRef = useRef(null);

  // Convert mouse wheel scrolling to horizontal scroll of search links
  useEffect(() => {
    const el = linksWrapperRef.current;
    // biome-ignore lint/suspicious/noEmptyBlockStatements: Fix me!
    if (!el) return () => {};

    const handleWheel = (e) => {
      // Skip small deltas (likely trackpad)
      if (Math.abs(e.deltaY) < 30) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };

    el.addEventListener('wheel', handleWheel, {passive: false});
    return () => el.removeEventListener('wheel', handleWheel, {passive: false});
  }, []);

  const gifs = useMemo(
    () =>
      results.length === 0 ? (
        <Typography variant="paragraph2">No results found.</Typography>
      ) : (
        <div className="giphy-search__results" id="giphy-search__results">
          {[0, 1, 2].map((col) => (
            <div className="giphy-search__col" key={col}>
              {results
                .filter((_, idx) => idx % 3 === col)
                .map((result, i) => (
                  <Button
                    variant="unstyled-button"
                    className="giphy-search__result"
                    // biome-ignore lint/suspicious/noArrayIndexKey: same image may be in the results more than once
                    key={result.id + i}
                    data-qa-id="giphy-search-result"
                    tabIndex={i + 1}
                    onClick={(e) => onHandleGifSelect?.(e, result.images.original.mp4)}
                  >
                    <img
                      className="giphy-search__result__img"
                      src={result.images.fixed_width_small.url}
                      alt={result.title}
                    />
                  </Button>
                ))}
            </div>
          ))}
        </div>
      ),
    [results, onHandleGifSelect]
  );

  return (
    <div className="giphy-search">
      <div className="giphy-search__header">
        <InputText
          className="giphy-search__search-input"
          isSearchable
          type="text"
          value={search || ''}
          placeholder="Search GIPHY..."
          maxLength={48}
          required={false}
          onChange={(e) => setSearch(e.target.value)}
          data-qa-id="giphy-search-input"
        />
        {search !== '' && (
          <Button
            className="giphy-search__reset-search-btn"
            data-qa-id="reset-giphy-search-btn"
            variant="unstyled-button"
            icon={<CloseCircleIcon color={cloud} ratio={0.5} />}
            onClick={() => setSearch('')}
          />
        )}
        <img
          className="giphy-search__giphy-logo"
          src="https://g0.evitecdn.com/create-flow/ui/powered-by-giphy-logo.png"
          alt="giphy logo"
        />
        <Link
          href="#giphy-search__results"
          className="giphy-search__skip-link"
          data-qa-id="giphy-search-skip-link"
        >
          Skip to results
        </Link>
        <div className="giphy-search__search-links__wrapper" ref={linksWrapperRef}>
          <div className="giphy-search__search-links">
            <Button
              variant="secondary"
              className={cx('giphy-search__search-link', {
                'giphy-search__search-link--active': search === '',
              })}
              data-qa-id="giphy-search-search-link"
              onClick={() => setSearch('')}
            >
              <Typography variant="paragraph4" color="charcoal" semibold>
                trending
              </Typography>
            </Button>
            {searchLinks.map((link) => (
              <Button
                variant="secondary"
                className={cx('giphy-search__search-link', {
                  'giphy-search__search-link--active': search === link,
                })}
                key={link}
                data-qa-id="giphy-search-search-link"
                onClick={() => setSearch(link)}
              >
                <Typography variant="paragraph4" color="charcoal" semibold>
                  {link}
                </Typography>
              </Button>
            ))}
          </div>
        </div>
      </div>
      <div className="giphy-search__body">
        <div ref={giphyResultsRef} className="giphy-search__results-wrapper">
          {loading && <span className="evt-spinner" />}
          {error && <Typography variant="paragraph3">{error}</Typography>}
          {!error && !loading && gifs}
        </div>
      </div>
    </div>
  );
};
